// Welcome Bonus — opening the bonus sheet by itself when a Player with an unclaimed bonus comes in.
//
// It opens on every visit (each page load, each sign-in) until the Player ticks "Don't show this
// again". That choice is kept per account: kiosk devices are shared, so one Player opting out must
// not hide the bonus from the next new Player on the same device.
//
// Within a visit it stays away only once the Player has closed it. Until then it is still owed:
// the header (and the badge in it) can remount while the page is loading, and a sheet that was
// merely "already opened once" was lost with the old header and never came back after a reload.

import { apiService } from '@/services/apiService';
import { accountIdFromToken } from '@/utils/accountId';
import type { SignupBonus } from './signupBonusService';
import { CONTACT_NOT_VERIFIED_CODE } from './signupBonusGating';

const HIDE_KEY_PREFIX = 'sb-welcome-prompt-hidden:';
const UNKNOWN_ACCOUNT = 'unknown';

/** Account that closed the sheet during this visit (in memory: a reload is a new visit). */
let dismissedAccount: string | null = null;

/** Stable id of the signed-in account, read from the access token payload (`userId`). */
function currentAccountId(): string {
  try {
    return accountIdFromToken(apiService.getAccessToken()) ?? UNKNOWN_ACCOUNT;
  } catch {
    return UNKNOWN_ACCOUNT;
  }
}

/** True when this account ticked "Don't show this again". */
export function isWelcomePromptHidden(): boolean {
  try {
    return localStorage.getItem(HIDE_KEY_PREFIX + currentAccountId()) === '1';
  } catch {
    return false;
  }
}

export function setWelcomePromptHidden(hidden: boolean): void {
  try {
    const key = HIDE_KEY_PREFIX + currentAccountId();
    if (hidden) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    /* storage unavailable (private mode): the sheet simply keeps opening */
  }
}

/**
 * Open the sheet by itself only when the Player can act on it: the bonus is unclaimed, offered,
 * and either claimable or waiting for the Player to verify a contact. A bonus blocked for a reason
 * they cannot fix (daily limit, shop debt…) is not pushed at them; the header badge still shows it.
 */
export function shouldAutoOpenWelcomeBonus(bonus: SignupBonus | null | undefined): boolean {
  if (!bonus || bonus.status !== 'inactive' || bonus.offered === false) return false;
  if (bonus.claimBlockCode && bonus.claimBlockCode !== CONTACT_NOT_VERIFIED_CODE) return false;
  if (dismissedAccount === currentAccountId()) return false;
  return !isWelcomePromptHidden();
}

/** The Player closed the sheet: it does not open by itself again until the next visit. */
export function markWelcomePromptDismissed(): void {
  dismissedAccount = currentAccountId();
}

/** Sign-out ends the visit, so the next sign-in (same or another account) is prompted again. */
export function resetWelcomePromptVisit(): void {
  dismissedAccount = null;
}
